import { useCallback, useEffect, useRef, useState } from 'react';

import {
  PROVIDER_WINDOW_CHECK_INTERVAL_MS,
  PROVIDER_WINDOW_FEATURES,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/constants';
import {
  AuthStepState,
  ProviderWindowState,
  type AuthStepInfo,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';

interface UseProviderAuthParams {
  /** OAuthUrl провайдера; null — ссылку не отдал бэк */
  authUrl: string | null;
  stepInfo: AuthStepInfo;
  isActive: boolean;
}

/**
 * Архетип «вход у провайдера» (ВКонтакте, Instagram — task/10731): вход идёт
 * в отдельном окне браузера, CRM ждёт успеха поллингом каркаса. Событий о
 * закрытии чужого окна браузер не шлёт — штатный способ узнать о нём это
 * опрос openedWindow.closed по интервалу (с очисткой).
 */
export const useProviderAuth = ({
  authUrl,
  stepInfo,
  isActive,
}: UseProviderAuthParams) => {
  const [windowState, setWindowState] = useState(ProviderWindowState.Idle);
  const providerWindowRef = useRef<Window | null>(null);
  const watchIntervalRef = useRef<number | null>(null);
  const openedForUrlRef = useRef<string | null>(null);

  const clearWatch = useCallback(() => {
    if (watchIntervalRef.current !== null) {
      window.clearInterval(watchIntervalRef.current);
      watchIntervalRef.current = null;
    }
  }, []);

  const openProviderWindow = useCallback(() => {
    if (!authUrl) return;

    const providerWindow = window.open(
      authUrl,
      '_blank',
      PROVIDER_WINDOW_FEATURES
    );

    // Браузер заблокировал попап — остаёмся в Idle, пользователь откроет
    // окно кнопкой (клик по ней попап-блокер уже не остановит).
    if (!providerWindow) {
      setWindowState(ProviderWindowState.Idle);
      return;
    }

    providerWindowRef.current = providerWindow;
    setWindowState(ProviderWindowState.Waiting);

    clearWatch();
    watchIntervalRef.current = window.setInterval(() => {
      if (providerWindowRef.current?.closed) {
        clearWatch();
        providerWindowRef.current = null;
        setWindowState(ProviderWindowState.Closed);
      }
    }, PROVIDER_WINDOW_CHECK_INTERVAL_MS);
  }, [authUrl, clearWatch]);

  // Первое окно — автоматически, как только появилась ссылка
  useEffect(() => {
    if (!isActive || !authUrl) return;
    // Интеграция уже подключена: окно открыто по шестерёнке ради настроек,
    // а не ради входа. Автоматически ничего не открываем — иначе окно
    // провайдера мигает и тут же закрывается эффектом успеха ниже.
    if (stepInfo.state === AuthStepState.Success) return;
    if (openedForUrlRef.current === authUrl) return;

    openedForUrlRef.current = authUrl;
    openProviderWindow();
  }, [isActive, authUrl, openProviderWindow, stepInfo.state]);

  // Успех пришёл поллингом — окно провайдера больше не нужно
  useEffect(() => {
    if (stepInfo.state !== AuthStepState.Success) return;

    clearWatch();
    providerWindowRef.current?.close();
    providerWindowRef.current = null;
  }, [stepInfo.state, clearWatch]);

  useEffect(() => clearWatch, [clearWatch]);

  return { windowState, openProviderWindow };
};
